import { useParams, Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { SEOHead, generateBreadcrumbSchema } from '@/components/base/SEO';
import { categories, comics } from '@/mocks/comics';
import { siteInfo } from '@/mocks/site';
import { useEffect } from 'react';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const categoryComics = comics.filter(c => c.category === (slug || categories[0].slug));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const canonicalUrl = `${siteInfo.url}/category/${currentCategory.slug}`;
  const breadcrumbItems = [
    { name: '首页', url: siteInfo.url },
    { name: currentCategory.name, url: canonicalUrl },
  ];

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 韩国漫画分类浏览`}
        description={`韩漫网${currentCategory.name}专区，${currentCategory.description}。收录${categoryComics.length}部精选韩国漫画，实时更新，免费在线阅读。`}
        keywords={`${currentCategory.name},韩国漫画,韩漫,无删减漫画,${currentCategory.name}漫画`}
        canonicalUrl={canonicalUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(generateBreadcrumbSchema(breadcrumbItems)) }}
      />

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: currentCategory.name }]} />

          <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar Categories */}
              <aside className="lg:w-56 flex-shrink-0">
                <h3 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>漫画分类</h3>
                <nav className="space-y-1">
                  {categories.map(cat => (
                    <RouterLink
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        cat.slug === currentCategory.slug
                          ? 'bg-primary-100 text-primary-600 font-semibold'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-primary-500'
                      }`}
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                      {cat.name}
                    </RouterLink>
                  ))}
                </nav>
              </aside>

              {/* Main Content */}
              <div className="flex-1">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                    {currentCategory.name}
                  </h1>
                  <p className="text-foreground-500 mt-2">{currentCategory.description} · 共 {categoryComics.length} 部作品</p>
                </div>

                {categoryComics.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                    {categoryComics.map(comic => (
                      <ComicCard key={comic.id} comic={comic} size="small" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <i className="ri-inbox-line text-5xl text-foreground-300 mb-4"></i>
                    <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                    <RouterLink to="/" className="inline-block mt-4 text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
                      返回首页
                    </RouterLink>
                  </div>
                )}

                {/* Pagination */}
                {categoryComics.length > 8 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap" disabled>
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-medium whitespace-nowrap cursor-pointer">1</button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">2</button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}